import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type NotifierService from "cc-frontend/services/notifier"
import type { EmberNotification } from "cc-frontend/services/notifier"

interface EmberNotifierNotificationSignature {
  Args: {
    notification: EmberNotification
    close: () => void
  }
  Blocks: {
    default: []
  }
  Element: HTMLDivElement
}

export default class EmberNotifierNotificationComponent extends Component<EmberNotifierNotificationSignature> {
  @service declare notifier: NotifierService

  private touchMeta: {
    startX: number
    startY: number
    startTime: number
    diffX: number
    diffY: number
    isHorizontal: boolean | null
    isInvalid: boolean
  } | null = null

  get typeClass() {
    return this.args.notification.type
  }

  get animationStateClass() {
    return this.args.notification.animationState
  }

  willDestroy() {
    super.willDestroy()
    if (this.args.notification.timer !== null) {
      this.notifier.cancelRemoval(this.args.notification)
    }
  }

  mouseEnter = () => {
    const { notification } = this.args
    if (notification.duration > 0) {
      this.notifier.cancelRemoval(notification)
    }
  }

  mouseLeave = () => {
    const { notification } = this.args
    if (notification.duration > 0) {
      this.notifier.scheduleRemoval(notification)
    }
  }

  touchStart = (event: TouchEvent) => {
    const touch = event.touches[0]
    if (!touch) return
    this.touchMeta = {
      startX: touch.pageX,
      startY: touch.pageY,
      startTime: Date.now(),
      diffX: 0,
      diffY: 0,
      isHorizontal: null,
      isInvalid: false,
    }
  }

  touchMove = (event: TouchEvent) => {
    if (!this.touchMeta) return
    const touch = event.touches[0]
    if (!touch) return
    this.touchMeta.diffX = touch.pageX - this.touchMeta.startX
    this.touchMeta.diffY = touch.pageY - this.touchMeta.startY
    if (this.touchMeta.isHorizontal === null) {
      this.touchMeta.isHorizontal = Math.abs(this.touchMeta.diffX) > Math.abs(this.touchMeta.diffY)
    }
    if (event.touches.length > 1) {
      this.touchMeta.isInvalid = true
    }
  }

  touchEnd = () => {
    const meta = this.touchMeta
    if (!meta) return
    const { notification } = this.args
    const elapsed = Date.now() - meta.startTime
    if (
      meta.isHorizontal &&
      !meta.isInvalid &&
      Math.abs(meta.diffX) >= notification.minSwipeDistance &&
      elapsed <= notification.maxSwipeTime
    ) {
      const direction = meta.diffX > 0 ? "right" : "left"
      if (notification.swipeDirection === direction) {
        this.args.close()
      }
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "EmberNotifier::Notification": typeof EmberNotifierNotificationComponent
  }
}
